/* Customer-facing plan pricing, mirrored from the billing source of truth in
   .claude/skills/invoice-build/scripts/price.py.
 *
 * These constants and the two functions below must stay identical to that file.
 * A website that quotes one number while the invoice charges another is worse
 * than having no calculator at all, so if price.py changes, change this too and
 * re-run `price.py selftest`.
 *
 * Deliberately NOT mirrored here: anything out of cost-rates.json. That file
 * holds what a stop COSTS Centerfire — labour rate, grease per tube, fluid per
 * gallon — not what it bills. It is margin data and must never be bundled into
 * a page a customer can read. The two were split apart on purpose; a public
 * calculator is exactly the thing that would quietly re-marry them.
 */

export const BASELINE = 40.0; // $/machine/month, the CFM subscription
export const MILE_RATE = 1.0;
export const TRIP_FACTOR = 1.5; // billable miles = 1.5x ONE-WAY from the shop
export const DISCOUNT_PER_MACHINE = 0.02;
export const DISCOUNT_FREE_MACHINES = 3;
/* Shallow on purpose: this discounts a hard cost, not a margin. Travel barely
   covers fuel plus the driver's time even undiscounted, so cutting deep into it
   does not shave profit, it stops the fee covering the driver. Binds from 8
   machines up, so any full-ish trip earns the whole 10%. */
export const DISCOUNT_CAP = 0.1;
export const MAX_MACHINES_PER_VISIT = 10;

/* Cumulative bands, applied in order: (through_count, price_each). Quoted on
   the page for reference only — greasing is an add-on and is not part of the
   estimate, because it needs a real zerk count per machine to mean anything. */
export const ZERK_TIERS = [
  [5, 5.0],
  [12, 3.5],
  [24, 2.0],
  [40, 1.0],
  [null, 0.75],
];

/** Whole percent off the callout for this many machines on one visit. */
export function discountPercent(machinesOnVisit) {
  const steps = Math.max(0, machinesOnVisit - DISCOUNT_FREE_MACHINES);
  const perStep = Math.round(DISCOUNT_PER_MACHINE * 100);
  return Math.min(Math.round(DISCOUNT_CAP * 100), perStep * steps);
}

/* A property past the daily ceiling is scheduled over several visits, and the
   machines are spread evenly rather than filling each visit to the brim — so
   16 machines is 8 + 8, not 15 and a lonely 1, and nobody's machine pays full
   freight on a visit of its own. */
export function splitEvenly(machines) {
  const visits = Math.ceil(machines / MAX_MACHINES_PER_VISIT);
  const base = Math.floor(machines / visits);
  const remainder = machines % visits;
  return Array.from({ length: visits }, (_, i) => base + (i < remainder ? 1 : 0));
}

/* Travel for ONE trip, in whole cents.
 *
 * Every trip is billed. A property needing two visits pays for two, because
 * every drive out burns fuel and a driver's day. That is why travel per machine
 * steps up when a property crosses MAX_MACHINES_PER_VISIT, and the step cannot
 * be discounted away — smoothing it requires the extra trip to be near free.
 * DISCOUNT_CAP protects the floor: a trip is never billed below (1 - cap) of
 * base, and since base scales with distance that floor tracks fuel.
 *
 * Integers on purpose. The exact figure is miles x 1.5 x (100 - pct)/100
 * dollars, which is miles x 3 x (100 - pct) / 2 cents — and that lands on an
 * exact half-cent often enough to matter. price.py flags 41 miles at 10
 * machines: 61.5 x 0.79 = 48.585, which binary floating point rounds DOWN to
 * 48.58 while the invoice rounds half-up to 48.59. Integer halves avoid the
 * whole question rather than papering over it with an epsilon. */
function tripCents(oneWayMiles, machinesOnTrip) {
  const numerator = oneWayMiles * 3 * (100 - discountPercent(machinesOnTrip));
  return numerator % 2 === 0 ? numerator / 2 : (numerator + 1) / 2;
}

/**
 * Monthly estimate for a property.
 *
 * `oneWayMiles` is the distance a customer actually knows — their own driveway
 * to the Bandera shop, one direction. The 1.5x is applied here, exactly as the
 * invoice applies it, so the number on screen is the number they get billed.
 */
export function estimate({ oneWayMiles, machines }) {
  const groups = splitEvenly(machines);
  const travel = groups.reduce((sum, onTrip) => sum + tripCents(oneWayMiles, onTrip), 0) / 100;
  const subscription = BASELINE * machines;

  return {
    groups,
    visits: groups.length,
    billableMiles: oneWayMiles * TRIP_FACTOR,
    discountPercent: discountPercent(groups[0]),
    travel,
    travelPerMachine: travel / machines,
    subscription,
    monthlyTotal: subscription + travel,
  };
}

export function money(value) {
  return `$${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}
